import { cn } from "../lib/utils";
import { Input } from "./ui/input";
import { DatePicker } from "./DatePicker";

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
    >
      <div>
        <div className="label-xs" style={{ color: "#fbbf24" }}>
          Back-office
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-[#a1a1aa] mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function KpiCard({ label, value, hint, tone = "default", testId }) {
  const toneColor = {
    default: "#ffffff",
    billets: "#34d399",
    jaunes: "#fbbf24",
    rouges: "#f87171",
    bleus: "#60a5fa",
    danger: "#f87171",
    success: "#34d399",
  }[tone];
  return (
    <div
      data-testid={testId}
      className="surface p-5 transition-all duration-200 hover:bg-[#1a1a21]"
    >
      <div className="label-xs">{label}</div>
      <div
        className="font-display text-3xl font-light tracking-tight mt-2"
        style={{ color: toneColor }}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-[#71717a] mt-1">{hint}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: { c: "#34d399", t: "Active" },
    archivee: { c: "#71717a", t: "Archivée" },
    inactive: { c: "#71717a", t: "Inactive" },
    actif: { c: "#34d399", t: "Actif" },
    termine: { c: "#71717a", t: "Terminé" },
    annule: { c: "#f87171", t: "Annulé" },
    brouillon: { c: "#71717a", t: "Brouillon" },
    a_valider: { c: "#fbbf24", t: "À valider" },
    valide: { c: "#34d399", t: "Validé" },
    validee: { c: "#34d399", t: "Validée" },
    conteste: { c: "#f87171", t: "Contesté" },
    non_regle: { c: "#71717a", t: "Non réglé" },
    demande_envoyee: { c: "#60a5fa", t: "Demande envoyée" },
    validation_manageuse: { c: "#fbbf24", t: "Validation manageuse" },
    a_payer: { c: "#fbbf24", t: "À payer" },
    paye: { c: "#34d399", t: "Payé" },
    payee: { c: "#34d399", t: "Payée" },
    refuse: { c: "#f87171", t: "Refusé" },
    en_retard: { c: "#ef4444", t: "En retard" },
  };
  const { c, t } = map[status] || { c: "#71717a", t: status };
  const isRetard = status === "en_retard";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
        isRetard ? "animate-pulse" : ""
      }`}
      style={{
        color: c,
        borderColor: c + (isRetard ? "" : "50"),
        background: c + (isRetard ? "25" : "14"),
      }}
    >
      {isRetard && <span className="mr-1">⚠</span>}
      {t}
    </span>
  );
}

export function DateRangeFilter({ start, end, onChange, testId = "date-filter" }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="label-xs">Période :</span>
      <DatePicker
        testId={`${testId}-start`}
        value={start || ""}
        onChange={(v) => onChange({ start: v, end })}
        placeholder="Début"
        className="h-9 w-[150px]"
      />
      <span className="text-[#71717a]">→</span>
      <DatePicker
        testId={`${testId}-end`}
        value={end || ""}
        onChange={(v) => onChange({ start, end: v })}
        placeholder="Fin"
        className="h-9 w-[150px]"
      />
      {(start || end) && (
        <button
          data-testid={`${testId}-clear`}
          onClick={() => onChange({ start: "", end: "" })}
          className="text-xs text-[#a1a1aa] hover:text-white underline"
        >
          effacer
        </button>
      )}
    </div>
  );
}

export function filterByDate(rows, dateKey, start, end) {
  if (!start && !end) return rows;
  return rows.filter((r) => {
    const d = r[dateKey];
    if (!d) return false;
    const iso = String(d).slice(0, 10);
    if (start && iso < start) return false;
    if (end && iso > end) return false;
    return true;
  });
}

export function EmptyState({ title, hint, icon: Icon }) {
  return (
    <div className="surface p-10 text-center">
      {Icon && (
        <div className="mx-auto w-12 h-12 rounded-full bg-[#1a1a21] flex items-center justify-center mb-3">
          <Icon size={22} color="#71717a" />
        </div>
      )}
      <div className="font-display text-lg">{title}</div>
      {hint && <div className="text-sm text-[#71717a] mt-1">{hint}</div>}
    </div>
  );
}

export function DataTable({ columns, rows, rowKey, onRowClick, emptyText = "Aucune donnée" }) {
  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "#27272a" }}>
              {columns.map((c, i) => (
                <th
                  key={c.key || c.title || `col-${i}`}
                  className={cn(
                    "label-xs text-left py-3 px-4 whitespace-nowrap",
                    c.align === "right" && "text-right"
                  )}
                  style={{ width: c.width }}
                >
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-12 text-[#71717a]">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr
                  key={rowKey ? r[rowKey] : i}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={cn(
                    "border-b last:border-b-0 transition-colors",
                    onRowClick && "cursor-pointer hover:bg-[#1a1a21]"
                  )}
                  style={{ borderColor: "#27272a" }}
                  data-testid={rowKey ? `row-${r[rowKey]}` : undefined}
                >
                  {columns.map((c, j) => (
                    <td
                      key={c.key || c.title || `cell-${j}`}
                      className={cn(
                        "py-3 px-4",
                        c.align === "right" && "text-right",
                        c.mono && "font-mono"
                      )}
                    >
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
